import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { API_BASE_URL } from '../config/environment';

export default function ScoreLookup() {
  const navigate = useNavigate();
  const { showQualifierLink } = useAuth();
  const [membershipId, setMembershipId] = useState('');
  const [scoreData, setScoreData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);



  const calculateTotalScore = (data) => {
    const sortedScores = data.competitions
      .map((comp) => comp.points)
      .sort((a, b) => b - a);
    const topThreeSum = sortedScores.slice(0, 3).reduce((sum, score) => sum + score, 0);
    let levelBonus = 0;
    return topThreeSum + levelBonus;
  };

  const getQualificationStatus = (data) => {
    if (!showQualifierLink) {
      return null; // Don't show qualification status if feature is disabled
    }

    const totalScore = calculateTotalScore(data);
    const competitionCount = data.competitions.length;
    
    // Basic requirements check
    if (competitionCount < 3) {
      return {
        status: 'incomplete',
        message: `Needs ${3 - competitionCount} more competition${3 - competitionCount === 1 ? '' : 's'}`,
        color: 'warning.main',
        bgColor: 'warning.50'
      };
    }

    // Check if they're likely to qualify (this is a simplified check)
    // In a real system, you'd compare against actual qualification thresholds
    if (totalScore >= 15) { // Assuming 15+ points is a good qualification score
      return {
        status: 'likely',
        message: 'Likely to qualify for championship',
        color: 'success.main',
        bgColor: 'success.50'
      };
    } else if (totalScore >= 10) {
      return {
        status: 'borderline',
        message: 'Borderline qualification status',
        color: 'info.main',
        bgColor: 'info.50'
      };
    } else {
      return {
        status: 'unlikely',
        message: 'Unlikely to qualify with current scores',
        color: 'warning.main',
        bgColor: 'warning.50'
      };
    }
  };

  const fetchScore = async () => {
    setIsLoading(true);
    setError(null);
    setScoreData(null);

    try {
      const response = await fetch(`${API_BASE_URL}/get-score/${membershipId}`);
      if (!response.ok) {
        throw new Error('Membership ID not found');
      }
      const data = await response.json();
      setScoreData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        maxWidth: '300px',
        mx: 'auto',
        gap: 2
      }}>
        <TextField
          label="USFSC Membership ID"
          variant="outlined"
          value={membershipId}
          onChange={(e) => setMembershipId(e.target.value)}
          disabled={isLoading}
          sx={{ width: '100%' }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={fetchScore}
          disabled={isLoading}
          sx={{ 
            textTransform: 'uppercase',
            minWidth: '100px',
            px: 3
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Search'}
        </Button>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          For more information visit{' '}
          <a href="http://www.pacificnwinterclub.com/basic-skills-series.html" target="_blank" rel="noreferrer">
            <u>PNIC Basic Skills</u>
          </a>
        </Typography>
      </Box>
      
      {error && (
        <Typography color="error" sx={{ marginTop: 2 }}>
          {error}
        </Typography>
      )}

      {scoreData && (
        <Box mt={4} p={2} border={1} borderColor="grey.300" borderRadius={2}>
          <Typography variant="h6" fontWeight="bold">
            {scoreData.name}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Club:</strong> {scoreData.skatingClub}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Starting Level:</strong> {scoreData.startingCompetitionLevel || 'Not Set'}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Last Competition Level:</strong> {scoreData.competitionLevel}
          </Typography>



          <Typography variant="h6" fontWeight="bold" mt={2}>
            Competition Points:
          </Typography>
          {scoreData.competitions.length === 0 ? (
            <ul>
              <li>
                <strong>No Competitions Recorded</strong>
              </li>
            </ul>
          ) : (
            <ul style={{ textAlign: 'left', paddingLeft: '20px' }}>
              {scoreData.competitions.map((comp, index) => (
                <li key={index}>
                  <strong>{comp.competitionName}:</strong> {comp.points} points
                </li>
              ))}
            </ul>
          )}

          <Typography variant="h6" fontWeight="bold" mt={2}>
            Total Score: {calculateTotalScore(scoreData)}
          </Typography>
          <Typography variant="subtitle2" mt={1} sx={{ mb: 2 }}>
            (Top 3 scores are used for qualification.)
          </Typography>

          {/* Helpful blurb about missing competitions */}
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: 'info.50',
            border: 1,
            borderColor: 'info.main'
          }}>
            <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 'medium' }}>
              💡 Missing a competition? Check the{' '}
              <Box
                component="span"
                onClick={() => navigate('/competitions')}
                sx={{ 
                  color: 'info.main', 
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'none'
                  }
                }}
              >
                Competitions page
              </Box>{' '}
              to see if it's been processed.
            </Typography>
          </Box>

          {/* Basic Requirements Check - Always shown */}
          <Typography variant="h6" fontWeight="bold" mt={3}>
            📋 Basic Requirements Check:
          </Typography>
          <Box sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: scoreData.competitions.length >= 3 ? 'success.50' : 'warning.50',
            border: 1,
            borderColor: scoreData.competitions.length >= 3 ? 'success.main' : 'warning.main'
          }}>
            <Typography variant="body1" sx={{ 
              color: scoreData.competitions.length >= 3 ? 'success.main' : 'warning.main',
              fontWeight: 'bold'
            }}>
              {scoreData.competitions.length >= 3 ? '✅ Requirements Met' : '⚠️ Requirements Not Met'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Competitions Competed:</strong> {scoreData.competitions.length}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Requirement:</strong> Minimum 3 competitions to be considered for championship
            </Typography>
            {scoreData.competitions.length >= 3 && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'success.main' }}>
                This skater has met the basic requirements and will be evaluated for championship qualification.
              </Typography>
            )}
            {scoreData.competitions.length < 3 && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                This skater needs {3 - scoreData.competitions.length} more competition{3 - scoreData.competitions.length === 1 ? '' : 's'} to meet the requirements.
              </Typography>
            )}
          </Box>

          {/* Detailed Championship Qualification Status - Only shown when qualifier feature is enabled */}
          {showQualifierLink && (() => {
            const qualStatus = getQualificationStatus(scoreData);
            if (!qualStatus) return null;
            
            return (
              <>
                <Typography variant="h6" fontWeight="bold" mt={3}>
                  🏆 Championship Qualification Analysis:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: qualStatus.bgColor,
                  border: 1,
                  borderColor: qualStatus.color
                }}>
                  <Typography variant="body1" sx={{ 
                    color: qualStatus.color,
                    fontWeight: 'bold'
                  }}>
                    {qualStatus.status === 'likely' && '✅ Likely to Qualify'}
                    {qualStatus.status === 'borderline' && '⚠️ Borderline Status'}
                    {qualStatus.status === 'unlikely' && '❌ Unlikely to Qualify'}
                    {qualStatus.status === 'incomplete' && '⏳ Requirements Incomplete'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Status:</strong> {qualStatus.message}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Total Score:</strong> {calculateTotalScore(scoreData)} points
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Competitions:</strong> {scoreData.competitions.length}/3 minimum
                  </Typography>
                  {qualStatus.status === 'incomplete' && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                      This skater needs {3 - scoreData.competitions.length} more competition{3 - scoreData.competitions.length === 1 ? '' : 's'} to meet the basic requirements.
                    </Typography>
                  )}
                  {qualStatus.status === 'likely' && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'success.main' }}>
                      Based on current scores, this skater appears to be in a strong position for championship qualification.
                    </Typography>
                  )}
                  {qualStatus.status === 'borderline' && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'info.main' }}>
                      This skater is close to qualification but may need additional competitions or higher scores.
                    </Typography>
                  )}
                  {qualStatus.status === 'unlikely' && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                      This skater may need to compete in more events or achieve higher scores to qualify.
                    </Typography>
                  )}
                </Box>
              </>
            );
          })()}
        </Box>
      )}
    </Box>
  );
}